/**
 * Auto Export Service
 * Tự động export flashcards ra file JSON trong public/data/ khi user save
 * 
 * ⚠️ LƯU Ý: Service này chỉ hoạt động trong DEV mode (localhost)
 * Không hoạt động trên production build
 */

import type { Flashcard, PDFDocument } from '../../types/flashcard'

interface ExportData {
  userEmail: string
  documents: PDFDocument[]
  flashcards: Flashcard[]
  lastUpdated: string
}

class AutoExportService {
  private readonly isDev = import.meta.env.DEV
  
  /**
   * Get user email from auth
   */
  private getUserEmail(): string {
    try {
      const userDataString = localStorage.getItem('google_auth_user')
      if (!userDataString) return 'quants82@gmail.com' // Default email
      
      const userData = JSON.parse(userDataString)
      return userData.email || 'quants82@gmail.com'
    } catch {
      return 'quants82@gmail.com'
    }
  }

  /**
   * Sanitize email for filename
   */
  private sanitizeEmail(email: string): string {
    return email.replace(/[^a-z0-9@.-]/gi, '_')
  }

  /**
   * Export flashcards to file system (DEV ONLY)
   * Tự động tạo file trong public/data/ khi save
   */
  async autoExport(flashcards: Flashcard[], documents: PDFDocument[]): Promise<void> {
    // Chỉ hoạt động trong DEV mode
    if (!this.isDev) {
      console.log('⚠️ Auto-export chỉ hoạt động trong DEV mode')
      return
    }

    try {
      const userEmail = this.getUserEmail()
      const filename = this.sanitizeEmail(userEmail) + '.json'

      // Tạo export data
      const exportData: ExportData = {
        userEmail,
        documents,
        flashcards,
        lastUpdated: new Date().toISOString()
      }

      const json = JSON.stringify(exportData, null, 2)

      console.log('📦 AUTO-EXPORT: Preparing to save...')
      console.log(`   Email: ${userEmail}`)
      console.log(`   Flashcards: ${flashcards.length}`)
      console.log(`   Documents: ${documents.length}`)

      // Gọi API để lưu file (chỉ trong dev)
      await this.saveToPublicDir(filename, json)

      console.log(`✅ AUTO-EXPORT: Saved to public/data/${filename}`)
      console.log('   Build lần sau sẽ tự động include file này!')

    } catch (error) {
      console.error('❌ Auto-export error:', error)
      // Không throw error để không ảnh hưởng save flashcards
    }
  }

  /**
   * Save file to public/data/ directory (DEV ONLY)
   * Sử dụng Vite's file system API hoặc download fallback
   */
  private async saveToPublicDir(filename: string, content: string): Promise<void> {
    // Trong DEV mode, ta có thể:
    // 1. Tạo file trong public/data/ thông qua Vite API (nếu có)
    // 2. Hoặc download file và nhắc user copy vào public/data/

    // Vì không thể write file trực tiếp từ browser, 
    // ta sẽ tự động download file và show instruction
    
    const blob = new Blob([content], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    
    // Tạo temporary link và download
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    
    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 100)

    // Show notification với instructions
    this.showAutoExportNotification(filename)
  }

  /**
   * Show notification về auto-export
   */
  private showAutoExportNotification(filename: string): void {
    console.log('')
    console.log('%c╔═══════════════════════════════════════════════════════════╗', 'color: #2563eb')
    console.log('%c║  📦 AUTO-EXPORT: File đã tải xuống!                      ║', 'color: #2563eb; font-weight: bold')
    console.log('%c╠═══════════════════════════════════════════════════════════╣', 'color: #2563eb')
    console.log('%c║  File: ' + filename.padEnd(51) + '║', 'color: #2563eb')
    console.log('%c║                                                           ║', 'color: #2563eb')
    console.log('%c║  ✅ ĐANG LÀM TỰ ĐỘNG:                                    ║', 'color: #16a34a; font-weight: bold')
    console.log('%c║  → File đã download vào thư mục Downloads                ║', 'color: #16a34a')
    console.log('%c║  → Copy file vào: public/data/' + filename.padEnd(26) + '║', 'color: #16a34a')
    console.log('%c║  → Build lần sau sẽ TỰ ĐỘNG include!                    ║', 'color: #16a34a')
    console.log('%c║                                                           ║', 'color: #2563eb')
    console.log('%c║  🎯 TIẾP THEO:                                            ║', 'color: #f59e0b; font-weight: bold')
    console.log('%c║  1. Mở thư mục Downloads                                 ║', 'color: #f59e0b')
    console.log('%c║  2. Copy file ' + filename.padEnd(43) + '║', 'color: #f59e0b')
    console.log('%c║  3. Paste vào: D:\\Ebook - thi\\public\\data\\           ║', 'color: #f59e0b')
    console.log('%c║  4. Chạy: npm run build                                  ║', 'color: #f59e0b')
    console.log('%c║  5. Deploy!                                              ║', 'color: #f59e0b')
    console.log('%c╚═══════════════════════════════════════════════════════════╝', 'color: #2563eb')
    console.log('')

    // Show browser notification nếu được phép
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('📦 Flashcards Auto-Exported!', {
        body: `File ${filename} đã tải xuống. Copy vào public/data/ để build.`,
        icon: '/favicon.ico'
      })
    }
  }

  /**
   * Check xem file đã tồn tại trong public/data/ chưa
   */
  async checkFileExists(userEmail?: string): Promise<boolean> {
    try {
      const email = userEmail || this.getUserEmail()
      const filename = this.sanitizeEmail(email) + '.json'
      const response = await fetch(`/data/${filename}`, { method: 'HEAD' })
      return response.ok
    } catch {
      return false
    }
  }

  /**
   * Load all flashcards và documents từ localStorage
   */
  getAllDataFromLocalStorage(): { flashcards: Flashcard[], documents: PDFDocument[] } {
    try {
      const flashcardsData = localStorage.getItem('vat_li_365_flashcards')
      const documentsData = localStorage.getItem('vat_li_365_pdf_documents')

      const flashcards: Flashcard[] = flashcardsData 
        ? JSON.parse(flashcardsData).map((f: any) => ({
            ...f,
            createdAt: new Date(f.createdAt),
            updatedAt: new Date(f.updatedAt),
            lastReviewed: f.lastReviewed ? new Date(f.lastReviewed) : undefined,
            nextReview: f.nextReview ? new Date(f.nextReview) : undefined
          }))
        : []

      const documents: PDFDocument[] = documentsData
        ? JSON.parse(documentsData).map((d: any) => ({
            ...d,
            uploadedAt: new Date(d.uploadedAt)
          }))
        : []

      return { flashcards, documents }
    } catch (error) {
      console.error('Error loading data from localStorage:', error)
      return { flashcards: [], documents: [] }
    }
  }

  /**
   * Request notification permission
   */
  async requestNotificationPermission(): Promise<void> {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission()
    }
  }
}

export const autoExportService = new AutoExportService()

// Expose to window for debugging
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).autoExportService = autoExportService
}

